import ArrowRightIcon from "@redotech/redo-icons/arrow-right.svg";
import {
  RedoBadge,
  type RedoBadgeColor,
} from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { formatSentenceCase } from "@redotech/util/string";
import { lazy, memo, Suspense, useCallback, useMemo, useState } from "react";
import { AskRedoConfirmation } from "../ask-redo-confirmation";
import type {
  FlowChangeDescription,
  FlowEditPreview,
} from "./parse-flow-edit-preview";

/** The canvas pulls in the whole flow editor, so it loads on first preview. */
const FlowChangePreviewModal = lazy(async () => ({
  default: (await import("./flow-change-preview-modal")).FlowChangePreviewModal,
}));

/**
 * Approval card for a return-flow edit: the per-step before/after diff plus a
 * preview of the proposed flow, so approving is not a blind accept.
 */
export const FlowEditPreviewCard = memo(function FlowEditPreviewCard({
  preview,
  onApprove,
  onDeny,
  onStageInEditor,
}: {
  preview: FlowEditPreview;
  onApprove: () => void;
  onDeny: () => void;
  /** Stage without publishing; resolves false when the save failed. */
  onStageInEditor?: () => Promise<boolean>;
}) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const openPreview = useCallback(() => setPreviewOpen(true), []);
  const closePreview = useCallback(() => setPreviewOpen(false), []);

  return (
    <>
      <AskRedoConfirmation
        approveLabel="Apply changes"
        denyLabel="Cancel"
        description={preview.confirmationPrompt}
        details={
          <Flex dir="column" gap="sm">
            {preview.changes?.length ? (
              <FlowChangeList changes={preview.changes} />
            ) : (
              <Text fontSize="sm" textColor="secondary" whiteSpace="pre-wrap">
                {preview.summary}
              </Text>
            )}
            <Flex justify="flex-start">
              <RedoButton
                hierarchy="secondary"
                onClick={openPreview}
                size="sm"
                text="View changes"
              />
            </Flex>
          </Flex>
        }
        onApprove={onApprove}
        onDeny={onDeny}
        title="Approve these flow changes?"
      />
      {previewOpen && (
        <Suspense fallback={null}>
          <FlowChangePreviewModal
            onClose={closePreview}
            onStageInEditor={onStageInEditor}
            open
            preview={preview}
          />
        </Suspense>
      )}
    </>
  );
});

interface StepChangeGroup {
  step: string;
  /** Whole-step changes rendered as a badge beside the step name. */
  statuses: FlowChangeDescription[];
  /** Field-level changes rendered as before → after rows. */
  fieldChanges: FlowChangeDescription[];
}

function groupChangesByStep(
  changes: FlowChangeDescription[],
): StepChangeGroup[] {
  const byStep = new Map<string, StepChangeGroup>();
  for (const change of changes) {
    let group = byStep.get(change.step);
    if (!group) {
      group = { step: change.step, statuses: [], fieldChanges: [] };
      byStep.set(change.step, group);
    }
    if (change.before === undefined && change.after === undefined) {
      group.statuses.push(change);
    } else {
      group.fieldChanges.push(change);
    }
  }
  return [...byStep.values()];
}

const STATUS_BADGES: Record<string, { text: string; color: RedoBadgeColor }> = {
  "step added": { text: "Added", color: "success" },
  "step removed": { text: "Removed", color: "error" },
};

const FlowChangeList = memo(function FlowChangeList({
  changes,
}: {
  changes: FlowChangeDescription[];
}) {
  const groups = useMemo(() => groupChangesByStep(changes), [changes]);
  return (
    <Flex dir="column" gap="sm">
      {groups.map((group) => (
        <Flex dir="column" gap="xs" key={group.step}>
          <Flex align="center" gap="xs" wrap="wrap">
            <Text fontSize="xs" fontWeight="semibold" textColor="primary">
              {group.step}
            </Text>
            {group.statuses.map((status, index) => {
              const badge = STATUS_BADGES[status.label];
              return (
                <RedoBadge
                  color={badge?.color ?? "gray"}
                  key={`${status.label}-${index}`}
                  size="xs"
                  text={badge?.text ?? formatSentenceCase(status.label)}
                />
              );
            })}
          </Flex>
          {group.fieldChanges.map((change, index) => (
            <Flex
              align="center"
              gap="xs"
              key={`${change.label}-${index}`}
              wrap="wrap"
            >
              <Text fontSize="xs" textColor="secondary">
                {change.label}
              </Text>
              {change.before !== undefined && (
                <Text
                  fontSize="xs"
                  textColor="error"
                  textDecoration="line-through"
                >
                  {change.before}
                </Text>
              )}
              {change.before !== undefined && change.after !== undefined && (
                <Flex aria-hidden="true" shrink={0}>
                  <RedoIcon color="tertiary" Icon={ArrowRightIcon} size={12} />
                </Flex>
              )}
              {change.after !== undefined && (
                <Text fontSize="xs" fontWeight="medium" textColor="success">
                  {change.after}
                </Text>
              )}
            </Flex>
          ))}
        </Flex>
      ))}
    </Flex>
  );
});
